import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Empty, Tooltip } from "antd";
import {
  PlusOutlined,
  UserOutlined,
  MergeOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import BillPanelNoContext from "./BillPanelNoContext";
import MergeTableModalNoContext from "./MergeTableModalNoContext";
import BillService from "../../services/Billservice";
import "./TableViewNoContext.css";

const TableCard = ({
  table,
  billCount,
  onCreateBill,
  onViewBills,
  onMerge,
  canCreateBill,
  canUpdateBill,
}) => {
  const isOccupied = table.status === "occupied";

  return (
    <Card
      className={`tb-card ${isOccupied ? "occupied" : "available"}`}
      hoverable
      bodyStyle={{ padding: 0 }}
    >
      <div className="tb-card-head">
        <div className="tb-card-title-wrap">
          <div className="tb-card-title">{table.name}</div>

          <div className="tb-card-meta">
            <span>
              <UserOutlined /> {table.capacity} Max
            </span>
            <span>{table.zone}</span>
          </div>
        </div>

        <div
          className={`tb-status-badge ${isOccupied ? "occupied" : "available"}`}
        >
          {isOccupied ? "Occupied" : "Available"}
        </div>
      </div>

      {isOccupied && (
        <div className="tb-bill-pill">
          <span className="tb-bill-count">{billCount}</span>
          <span>{billCount === 1 ? "Active Bill" : "Active Bills"}</span>
        </div>
      )}

      <div className="tb-card-actions">
        {isOccupied ? (
          <>
            {canCreateBill && (
              <button
                className="tb-btn secondary"
                onClick={() => onCreateBill(table.id)}
                type="button"
              >
                <PlusOutlined />
                New Bill
              </button>
            )}

            <button
              className="tb-btn primary"
              onClick={() => onViewBills(table.id)}
              type="button"
            >
              <EyeOutlined />
              View Bills
            </button>

            {canUpdateBill && (
              <Tooltip title="Merge to another table">
                <button
                  className="tb-btn secondary icon-only"
                  onClick={() => onMerge(table.id)}
                  type="button"
                >
                  <MergeOutlined />
                </button>
              </Tooltip>
            )}
          </>
        ) : canCreateBill ? (
          <button
            className="tb-btn primary"
            style={{ width: "100%" }}
            onClick={() => onCreateBill(table.id)}
            type="button"
          >
            <PlusOutlined />
            Open Bill
          </button>
        ) : (
          <button
            className="tb-btn primary"
            style={{ width: "100%", opacity: 0.6, cursor: "not-allowed" }}
            type="button"
            disabled
          >
            <PlusOutlined />
            Open Bill
          </button>
        )}
      </div>
    </Card>
  );
};

const TableViewNoContext = ({
  tables = [],
  menuItems = [],
  categories = [],
  getActiveBillsForTable,
  createBill,
  billModalState,
  setBillModalState,
  getTableById,
  deleteBill,
  getBillTotal,
  mergeTables,
  mergeBillsInTable,
  splitBill,
  updateBillItems,
  updateBillMeta,
  closeBill,
  refreshBillsForTable,
  canCreateBill = false,
  canUpdateBill = false,
  canDeleteBill = false,
  generalSettings = {},
}) => {
  const [zone, setZone] = useState("All");
  const [mergeSource, setMergeSource] = useState(null);
  const [zones, setZones] = useState([{ value: "All", label: "All" }]);

  useEffect(() => {
    const fetchZones = async () => {
      try {
        const res = await BillService.getAllZones();
        const zoneList = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res)
            ? res
            : [];

        const formattedZones = zoneList.map((item) => ({
          value: item.name || item.zone_name || item.zone || item.id,
          label: item.name || item.zone_name || item.zone || `Zone ${item.id}`,
        }));

        setZones([{ value: "All", label: "All" }, ...formattedZones]);
      } catch (error) {
        console.error("Failed to fetch zones:", error);
        setZones([{ value: "All", label: "All" }]);
      }
    };

    fetchZones();
  }, []);

  const filteredTables = useMemo(() => {
    if (zone === "All") return tables;
    return tables.filter((t) => t.zone === zone);
  }, [tables, zone]);

  const handleCreateBill = async (tableId) => {
    if (!canCreateBill) return;

    const newBill = await createBill(tableId);

    if (!newBill && newBill !== null) return;

    setBillModalState({
      open: true,
      tableId,
      billId: newBill?.id ?? null,
    });
  };

  const handleViewBills = (tableId) => {
    setBillModalState({
      open: true,
      tableId,
      billId: null,
    });
  };

  const handleMerge = (tableId) => {
    if (!canUpdateBill) return;
    setMergeSource(tableId);
  };

  const handleClosePanel = () => {
    setBillModalState({
      open: false,
      tableId: null,
      billId: null,
    });
  };

  const selectedTable = billModalState?.tableId
    ? getTableById?.(billModalState.tableId)
    : null;

  const selectedBills = useMemo(() => {
    if (!billModalState?.tableId) return [];
    const bills = getActiveBillsForTable?.(billModalState.tableId) || [];

    return bills.map((bill) => {
      const isEmptyBill = !Array.isArray(bill?.items) || bill.items.length === 0;
      if (!isEmptyBill) return bill;

      return {
        ...bill,
        paid: 0,
      };
    });
  }, [billModalState?.tableId, getActiveBillsForTable]);

  return (
    <div className="tables-bills-page">
      <div className="page-card table-card">
        <div className="table-card-header">
          <div>
            <h3>Table Overview</h3>
            <p>Filter tables by zone and manage bills quickly.</p>
          </div>
        </div>

        <div className="tb-toolbar">
          <div className="tb-filter-group">
            {zones.map((item) => (
              <button
                key={item.value}
                className={`tb-chip ${zone === item.value ? "active" : ""}`}
                onClick={() => setZone(item.value)}
                type="button"
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="tb-legend">
            <div className="tb-legend-item">
              <span className="tb-dot available" />
              Available
            </div>
            <div className="tb-legend-item">
              <span className="tb-dot occupied" />
              Occupied
            </div>
          </div>
        </div>

        {filteredTables.length === 0 ? (
          <div className="page-empty">
            <Empty description="No tables in this zone" />
          </div>
        ) : (
          <div className="tb-grid-wrap">
            <Row gutter={[18, 18]}>
              {filteredTables.map((table) => {
                const bills = getActiveBillsForTable?.(table.id) || [];

                return (
                  <Col key={table.id} xs={24} sm={12} md={8} lg={6} xl={6}>
                    <TableCard
                      table={table}
                      billCount={bills.length}
                      onCreateBill={handleCreateBill}
                      onViewBills={handleViewBills}
                      onMerge={handleMerge}
                      canCreateBill={canCreateBill}
                      canUpdateBill={canUpdateBill}
                    />
                  </Col>
                );
              })}
            </Row>
          </div>
        )}
      </div>

      {billModalState?.open && selectedTable && (
        <BillPanelNoContext
          table={selectedTable}
          bills={selectedBills}
          initialBillId={billModalState.billId}
          onClose={handleClosePanel}
          menuItems={menuItems}
          categories={categories}
          onAddBill={canCreateBill ? createBill : undefined}
          onDeleteBill={canDeleteBill ? deleteBill : undefined}
          onMergeBills={canUpdateBill ? mergeBillsInTable : undefined}
          onSplitBill={canUpdateBill ? splitBill : undefined}
          onUpdateBillItems={canUpdateBill ? updateBillItems : undefined}
          onUpdateBillMeta={canUpdateBill ? updateBillMeta : undefined}
          onCloseBill={canUpdateBill ? closeBill : undefined}
          getBillTotal={getBillTotal}
          refreshBillsForTable={refreshBillsForTable}
          canCreateBill={canCreateBill}
          canUpdateBill={canUpdateBill}
          canDeleteBill={canDeleteBill}
          generalSettings={generalSettings}
        />
      )}

      {mergeSource && canUpdateBill && (
        <MergeTableModalNoContext
          sourceTableId={mergeSource}
          tables={tables}
          getTableById={getTableById}
          getActiveBillsForTable={getActiveBillsForTable}
          onMergeTables={mergeTables}
          onClose={() => setMergeSource(null)}
        />
      )}
    </div>
  );
};

export default TableViewNoContext;
